import {
  useModalContext,
  WSButton,
  WSButtons,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSModal,
  WSRadioInputGroup,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";

import styles from "./styles.module.scss";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import {
  BulkStatus,
  CalculationSettings1099Toggle
} from "@wingspanhq/payments/dist/interfaces";
import { usePayrollSettings } from "../../query/payments/queries";
import { useUserId } from "../../query/hooks/helpers";
import { WSQueries } from "../../query/WSQuery";
import {
  useCreateBulkCalculate1099Batch,
  useUpdateBulkCalculate1099Batch,
  useUpdatePayrollSettings
} from "../../query/payments/mutations";
import { useUpdateActivity } from "../../query/users/mutations";
import { get1099SetupProgress } from "../../query/users/selectors";
import { useActivities } from "../../query/users/queries";

const OFF_PLATFORM_PAYMENTS_MODAL = "OFF_PLATFORM_PAYMENTS_MODAL";
const CARDS_PROCESSING_FEES_MODAL = "CARDS_PROCESSING_FEES_MODAL";
const REIMBURSABLE_EXPENSES_MODAL = "REIMBURSABLE_EXPENSES_MODAL";

const OffPlatformPaymentsModal = () => {
  return (
    <WSModal size="S" name={OFF_PLATFORM_PAYMENTS_MODAL}>
      <WSText.Heading4 mb="S">Off-platform payments</WSText.Heading4>
      <WSText.Paragraph color="grey700" mb="S">
        Invoices in Wingspan can be marked as paid off platform. That means that
        the invoices were paid, but Wingspan was not the payment processor. Both
        you and your collaborators can mark invoices as paid off platform.
      </WSText.Paragraph>
      <WSText.Paragraph>
        Invoices marked as paid off platform are useful to track and include
        payments in the 1099 amount from before you started using Wingspan or
        for when you've made payments to contractors directly.
      </WSText.Paragraph>
    </WSModal>
  );
};

const CardsProcessingFeesModal = () => {
  return (
    <WSModal size="S" name={CARDS_PROCESSING_FEES_MODAL}>
      <WSText.Heading4 mb="S">Card processing fees</WSText.Heading4>
      <WSText.Paragraph color="grey700" mb="S">
        If you've paid your contractors via a debit or credit card, you can
        choose to include the payment processing fee in the reported
        non-employee compensation.
      </WSText.Paragraph>
      <WSText.Paragraph>
        Wingspan recommends that you consult a tax advisor to determine your tax
        filing and reporting requirements.
      </WSText.Paragraph>
    </WSModal>
  );
};

const ReimbursableExpensesModal = () => {
  return (
    <WSModal size="S" name={REIMBURSABLE_EXPENSES_MODAL}>
      <WSText.Heading4 mb="S">Reimbursable expenses</WSText.Heading4>
      <WSText.Paragraph color="grey700" mb="S">
        You can choose to include or exclude line items marked as reimbursable
        expenses from the non-employee compensation amount.
      </WSText.Paragraph>
      <WSText.Paragraph>
        Wingspan recommends that you consult a tax advisor to determine your tax
        filing and reporting requirements.
      </WSText.Paragraph>
    </WSModal>
  );
};

interface CalculationSettingsFormValues {
  offPlatformPayments: CalculationSettings1099Toggle;
  cardProcessingFees: CalculationSettings1099Toggle;
  reimbursableExpenses: CalculationSettings1099Toggle;
}

export const CalculationSettings: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const history = useHistory();
  const userId = useUserId();
  const { openModal } = useModalContext();
  const qActivity = useActivities(userId);

  const [updateActivity, updateActivityMeta] = useUpdateActivity(userId);
  const [
    updatePayrollSettings,
    updatePayrollSettingsMeta
  ] = useUpdatePayrollSettings(userId);
  const qPayrollSettings = usePayrollSettings(userId);

  const onFormSubmit = async (data: CalculationSettingsFormValues) => {
    await updatePayrollSettings({
      calculationSettings1099: {
        offPlatformPayments: data.offPlatformPayments,
        cardProcessingFees: data.cardProcessingFees,
        reimbursableExpenses: data.reimbursableExpenses
      }
    });

    const nec1099SetupProgress = get1099SetupProgress(qActivity.data);
    await updateActivity({
      flows: {
        nec1099Setup: {
          ...nec1099SetupProgress,
          currentStep: 5
        }
      }
    });

    onNext();
  };
  return (
    <NEC1099FilingContainer title="Calculation settings">
      <OffPlatformPaymentsModal />
      <CardsProcessingFeesModal />
      <ReimbursableExpensesModal />
      <WSQueries queries={{ qPayrollSettings }}>
        {({ qPayrollSettings: { data: payrollSettings } }) => {
          const { calculationSettings1099 } = payrollSettings;
          return (
            <WSForm<CalculationSettingsFormValues>
              defaultValues={{
                offPlatformPayments:
                  calculationSettings1099?.offPlatformPayments,
                cardProcessingFees: calculationSettings1099?.cardProcessingFees,
                reimbursableExpenses:
                  calculationSettings1099?.reimbursableExpenses
              }}
              validationSchema={Yup.object().shape({
                offPlatformPayments: Yup.string().required("Required"),
                cardProcessingFees: Yup.string().required("Required"),
                reimbursableExpenses: Yup.string().required("Required")
              })}
              onSubmit={onFormSubmit}
            >
              <WSForm.Label>
                <WSFlexBox.CenterY mb="S">
                  <WSText color="grey600">Off-platform payments</WSText>
                  <WSIcon
                    ml="XS"
                    size="XS"
                    inline
                    name="info"
                    onClick={() => openModal(OFF_PLATFORM_PAYMENTS_MODAL)}
                  />
                </WSFlexBox.CenterY>
              </WSForm.Label>
              <WSForm.Field
                mb="XL"
                name="offPlatformPayments"
                component={WSRadioInputGroup}
                componentProps={{
                  disabled:
                    calculationSettings1099?.offPlatformPayments ===
                    CalculationSettings1099Toggle.Include,
                  optionHasBorder: true,
                  options: [
                    {
                      label: "Include invoices paid outside of Wingspan",
                      value: CalculationSettings1099Toggle.Include
                    },
                    {
                      label: "Exclude invoices paid outside of Wingspan",
                      value: CalculationSettings1099Toggle.Exclude
                    }
                  ]
                }}
              />

              <WSForm.Label>
                <WSFlexBox.CenterY mb="S">
                  <WSText color="grey600">Card processing fees</WSText>
                  <WSIcon
                    ml="XS"
                    size="XS"
                    inline
                    name="info"
                    onClick={() => openModal(CARDS_PROCESSING_FEES_MODAL)}
                  />
                </WSFlexBox.CenterY>
              </WSForm.Label>
              <WSForm.Field
                mb="XL"
                name="cardProcessingFees"
                component={WSRadioInputGroup}
                componentProps={{
                  optionHasBorder: true,
                  options: [
                    {
                      label: "Include card processing fees",
                      value: CalculationSettings1099Toggle.Include
                    },
                    {
                      label: "Exclude card processing fees",
                      value: CalculationSettings1099Toggle.Exclude
                    }
                  ]
                }}
              />

              <WSForm.Label>
                <WSFlexBox.CenterY mb="S">
                  <WSText color="grey600">Reimbursable expenses</WSText>
                  <WSIcon
                    ml="XS"
                    size="XS"
                    inline
                    name="info"
                    onClick={() => openModal(REIMBURSABLE_EXPENSES_MODAL)}
                  />
                </WSFlexBox.CenterY>
              </WSForm.Label>
              <WSForm.Field
                mb="XL"
                name="reimbursableExpenses"
                component={WSRadioInputGroup}
                componentProps={{
                  optionHasBorder: true,
                  options: [
                    {
                      label: "Include reimbursable expenses",
                      value: CalculationSettings1099Toggle.Include
                    },
                    {
                      label: "Exclude reimbursable expenses",
                      value: CalculationSettings1099Toggle.Exclude
                    }
                  ]
                }}
              />

              <WSButtons mt="XL">
                <WSButton.Secondary
                  className={styles.actionBtn}
                  onClick={onBack}
                >
                  Back
                </WSButton.Secondary>
                <WSForm.SubmitButton
                  className={styles.actionBtn}
                  name="submitCalculationSettings"
                  loading={
                    updateActivityMeta.isLoading ||
                    updatePayrollSettingsMeta.isLoading
                  }
                >
                  Continue
                </WSForm.SubmitButton>
              </WSButtons>
            </WSForm>
          );
        }}
      </WSQueries>
    </NEC1099FilingContainer>
  );
};
